import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { formatExactTimestamp } from '../../lib/inboxView';
import { providerVocabulary } from '../../lib/leadRelay/providerVocabulary';
import type { LeadRouteListItem } from '../../types/leadRelay';

// The Lead Routes table (docs/lead-relay.md §10.1) — presentational, so it is
// server-rendered in its test rather than mounted in jsdom.
//
// ⚠️ THE PROVIDER IS A COLUMN. Two routes that look alike and behave
// differently — a BYD store and an Anytime Fitness club on the same account
// list — is the condition under which someone edits the wrong one.
//
// ⚠️ The token column is a VirtualYard fact. A route of a provider with no
// form token reads as "—" (not applicable), never as the red "none" that means
// a VirtualYard route is missing its token.

export function LeadRoutesTable({
  routes,
  onOpen,
}: {
  routes: readonly LeadRouteListItem[];
  onOpen: (routeId: string) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Route</TableHead>
          <TableHead>Provider</TableHead>
          <TableHead>Account</TableHead>
          <TableHead>Store / club id</TableHead>
          <TableHead>Model map</TableHead>
          <TableHead>Meta forms</TableHead>
          <TableHead>State</TableHead>
          <TableHead>Token</TableHead>
          <TableHead>Last lead</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {routes.map((route) => {
          const vocab = providerVocabulary(route.provider);
          return (
            <TableRow key={route.id} className="cursor-pointer" onClick={() => onOpen(route.id)} data-testid="lead-route-row">
              <TableCell className="font-medium">{route.name}</TableCell>
              <TableCell data-testid="lead-route-provider">{vocab.system}</TableCell>
              <TableCell>{route.accountName ?? <span className="text-red-700">Unknown account</span>}</TableCell>
              <TableCell className="font-mono text-xs" title={vocab.destinationId}>
                {route.lid}
              </TableCell>
              <TableCell data-testid="lead-route-model-map">
                {/* ⚠️ A diverged route is visible WITHOUT opening it (§25). */}
                {route.modelMapOverrideCount === null ? (
                  <span className="text-muted-foreground">—</span>
                ) : route.modelMapStandardLabel === null ? (
                  <span>Own map</span>
                ) : route.modelMapOverrideCount === 0 ? (
                  <span>{route.modelMapStandardLabel}</span>
                ) : (
                  <span className="font-medium text-amber-700">
                    {route.modelMapStandardLabel} · {route.modelMapOverrideCount} override
                    {route.modelMapOverrideCount === 1 ? '' : 's'}
                  </span>
                )}
              </TableCell>
              <TableCell>
                {route.metaFormNames.length === 0 ? (
                  <span className="text-muted-foreground">None — no suggestion for unassigned leads</span>
                ) : (
                  route.metaFormNames.join(', ')
                )}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  <Badge variant={route.active ? 'success' : 'outline'}>{route.active ? 'Active' : 'Inactive'}</Badge>
                  {route.testMode && <Badge variant="secondary">Test mode</Badge>}
                  {route.testMode && route.inFlightCount > 0 && (
                    <Badge variant="warning" title="Queued before the route was put in test mode — they will still be submitted.">
                      {route.inFlightCount} still submitting
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-mono text-xs">
                {!vocab.hasFormToken ? (
                  <span className="text-muted-foreground" title={`${vocab.system} routes have no form token`}>
                    —
                  </span>
                ) : route.formUrlTokenTail ? (
                  `…${route.formUrlTokenTail}`
                ) : (
                  <span className="text-red-700">none</span>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {route.lastLeadReceivedAt ? (
                  formatExactTimestamp(route.lastLeadReceivedAt)
                ) : (
                  <span className="text-muted-foreground">Never</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
